import { includes, get, pick } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSelectedModelId, getShowNotHeld, getIsExpanded } from "modules/portfolio-root/selectors/portfolios"
import { getBasePositionSetWithSegment, getBaseSignature } from "selectors/base"
import { getSortedContributors, getSortedGroupContributors, getSelectedGroupValue, getSelectedGroup, getAvailableRollups, getContributorsSummaryText, getCsvData, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings } from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { getSegment } from "modules/analyze-header/selectors/segment"
import { getMissingDataFiles, betasAreMissing } from "selectors/missingDataFiles"
import { classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { getIsOpPro } from "modules/account-root/selectors"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { getPortfolioBetaInstrument } from "modules/portfolio-root/selectors/beta-instrument"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    portfolioId: state.global.selected.portfolio,
    modelId: getSelectedModelId(state, props),
    base: getBasePositionSetWithSegment(state, props),
    baseSignature: getBaseSignature(state, props),
    segment: getSegment(state, props),
    missingDataFiles: getMissingDataFiles(state, props),
    classifications: getClassifications(state, props),
    includeNotHeld: getIncludeNotHeld(state, props),
    expand: getIsExpanded(state, props),
    betaInstrument: getPortfolioBetaInstrument(state, props),
  }),
  required: ["portfolioId", "account", "missingDataFiles"],
  data: [{
    resource: "betaContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSignature, segment, missingDataFiles, classifications, includeNotHeld }) => {
      if (betasAreMissing(missingDataFiles, modelId)) { return null }
      if (selectedDate) {
        return {
          id: `${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
          query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                betaContributors(includeNotHeld: $includeNotHeld) {
                  date
                  contributors {
                    id
                    assetClass
                    assetSubclass
                    country
                    notHeld
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    percentEquity
                    bloombergTicker
                    modelProviderId
                    ${!includes(get(missingDataFiles, modelId), "predictedBeta") ? "predicted" : ""}
                    ${!includes(get(missingDataFiles, modelId), "historicalBeta") ? "historical" : ""}
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            portfolioId,
            date: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            segment,
            includeNotHeld,
            expand,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "groupedBetaContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSignature, contributorsGrouping, segment, classifications, includeNotHeld }) => {
      if (!contributorsGrouping) {
        return null
      }
      const queryGroupBy = getQueryGroupBy(contributorsGrouping, viewType === VIEW_TYPES.ACTIVE)
      return {
        id: `groupedBetaContributors-${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $portfolioId: String!, $expand: Boolean, $date: Date!, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                groupedBetaContributors (groupBy: $groupBy, classificationTier: $tier, groupById: $groupById, includeNotHeld: $includeNotHeld) {
                  date
                  groupedContributors {
                    name
                    id
                    totalPercentEquity
                    historical
                    predicted
                    contributors {
                      id
                      assetClass
                      country
                      notHeld
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      currency
                      description
                      percentEquity
                      historical
                      predicted
                      bloombergTicker
                      modelProviderId
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
        },
      }
    },
  }, {
    resource: "dynamicBetaContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSignature, segment, includeNotHeld, betaInstrument }) => {
      if (!betaInstrument || !selectedDate) { return null }
      return {
        id: `${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}${JSON.stringify(betaInstrument)}`,
        query: `query ($modelId: String!, $portfolioId: String!, $date: Date!, $expand: Boolean, $base: [PositionSetInput], $segment: Segment, $includeNotHeld: Boolean, $betaInstrument: PositionSetInput) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                betaContributors(includeNotHeld: $includeNotHeld, instrument: $betaInstrument) {
                  date
                  contributors {
                    id
                    dynamicPredicted: predicted
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          betaInstrument: pick(betaInstrument, ["type", "id"]),
        },
      }
    },
  }, {
    resource: "dynamicGroupedBetaContributors",
    as: null,
    url: ({ portfolioId, modelId, selectedDate, viewType, expand, base, baseSignature, contributorsGrouping, segment, includeNotHeld, betaInstrument }) => {
      if (!selectedDate || !contributorsGrouping || !betaInstrument) {
        return null
      }
      const queryGroupBy = getQueryGroupBy(contributorsGrouping, viewType === VIEW_TYPES.ACTIVE)
      return {
        id: `groupedBetaContributors-${modelId}${portfolioId}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}${JSON.stringify(betaInstrument)}`,
        query: `query ($modelId: String!, $portfolioId: String!, $expand: Boolean, $date: Date!, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $segment: Segment, $includeNotHeld: Boolean, $betaInstrument: PositionSetInput) {
            model (id: $modelId) {
              simulation (positionSet: {
                type: PORTFOLIO,
                id: $portfolioId,
                segment: $segment,
                expand: $expand
              }, from: $date, to: $date, base: $base, interval: END_DATE) {
                groupedBetaContributors (groupBy: $groupBy, classificationTier: $tier, groupById: $groupById, includeNotHeld: $includeNotHeld, instrument: $betaInstrument) {
                  date
                  groupedContributors {
                    name
                    id
                    totalPercentEquity
                    dynamicPredicted: predicted
                    contributors {
                      id
                      dynamicPredicted: predicted
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          portfolioId,
          date: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          segment,
          includeNotHeld,
          expand,
          ...queryGroupBy,
          betaInstrument: pick(betaInstrument, ["type", "id"]),
        },
      }
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => {
      return {
        id: "classifications",
        query: classificationsQuery,
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    csvData: getCsvData(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    isOpPro: getIsOpPro(state, props),
    showNotHeld: getShowNotHeld(state, props),
    groupBy: getGroupBy(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    ...state.modules.betaContributors,
  }),
  actions: {
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
